const WORD_LENGTH_TO_ABBREVIATE = 7;
const LINE_LENGTH_TO_BREAK = 8;

/**
 * Abbreviates all words in the input string with more than 7 characters.
 * @param string
 * @param abbreviationLength Optional length to abbreviate words to (default is 7)
 */
export const abbreviate = (string: string, abbreviationLength?: number): string => {
    if (!string) return '';

    const words = string.split(/\s+/g);
    const maxLength = abbreviationLength || WORD_LENGTH_TO_ABBREVIATE;
    return words
        .map((word: string) => {
            return word.length > maxLength ? `${word.slice(0, maxLength)}.` : word;
        })
        .join(' ');
};

/**
 * Returns the input string with the first letter capitalized.
 * @param string
 */
export const capitalize = (string: string): string => {
    if (!string) return '';

    return `${string[0].toUpperCase()}${string.slice(1)}`;
};

/**
 * Returns the input string with the first letter of each word capitalized.
 * @param s
 */
export const toAlphaCase = (s: string): string => {
    return s.replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Inserts a </br> element after every 8 characters.
 * @param string
 */
export const wordBreak = (string: string): string => {
    if (!string) return '';

    const words = string.split(/\s+/g);

    const wordsWithBreak = [];
    let currWordStart = 0;
    let currLineLength = words[0].length;

    for (let i = 1; i < words.length; i += 1) {
        const word = words[i];
        currLineLength += 1 + word.length; // one for the space

        // push current line
        if (currLineLength > LINE_LENGTH_TO_BREAK) {
            const line = words.slice(currWordStart, i).join(' ');
            wordsWithBreak.push(`${line}<br />`);

            currWordStart = i;
            currLineLength = word.length;
        }
    }

    // push whatever that's left
    const line = words.slice(currWordStart).join(' ');
    wordsWithBreak.push(line);

    return wordsWithBreak.join('');
};

/**
 * Adds leading zeros to make the input value the desired length.
 * @param inputVal: string or number to pad
 * @param outputLength: number (defaults to 2)
 */
export const pad = (inputVal: number | string, outputLength?: number): string => {
    let val = inputVal;
    val = String(val);
    const len = outputLength || 2;
    while (val.length < len) val = `0${val}`;
    return val;
};

/**
 * Removes trailing characters from the end of a string (whitespace by default).
 * @param str
 * @param chars Optional character set to trim (defaults to whitespace)
 */
export const rtrim = (str: string, chars?: string): string => {
    const newChars = chars || '\\s';
    return str.replace(new RegExp(`[${newChars}]+$`, 'g'), '');
};

/**
 * Replaces urls in the text with anchor tags.
 * @param text
 */
export const urlify = (text: string): string => {
    const urlRegex: RegExp = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, '<a href="$1" class="text-blue" target="_blank">$1</a>');
};

/**
 * Formats a change value with '+' or '-' prepended
 * @param inputVal
 */
export const formatChange = (inputVal: number | string): string => {
    if (typeof inputVal === 'string' && inputVal.length > 0) {
        if (inputVal.charAt(0) === '-') {
            return inputVal;
        }
        return '+'.concat(inputVal);
    }
    if (typeof inputVal === 'number') {
        if (inputVal > 0.0) {
            return '+'.concat(inputVal.toFixed(2));
        }
        if (inputVal < 0.0) {
            return inputVal.toFixed(2);
        }
    }

    return '+0.00';
};

export const stringToNumberOrNull = (val: string | null): number | null => {
    if (val == null) {
        return null;
    }
    const numberString = val.trim();
    if (numberString === '') {
        return null;
    }
    const number = Number(val);
    if (Number.isNaN(number)) {
        return null;
    }
    return number;
};
